import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Server, Key, Shield, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SuperAdminSettingsPage() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="super-admin-settings">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Configurações</h2>
        <p className="text-gray-500">Configure os serviços da plataforma</p>
      </div>

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="email">Serviço de Email</TabsTrigger>
          <TabsTrigger value="security">Segurança</TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <EmailSettings />
        </TabsContent>

        <TabsContent value="security">
          <SecuritySettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EmailSettings() {
  const [settings, setSettings] = useState({
    provider: 'resend',
    resend_api_key: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
    smtp_tls: true
  });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/super/email-settings`, { headers: getSuperAdminHeader() });
        if (response.data) {
          setSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          toast.error('Erro ao carregar configurações de e-mail');
        }
        console.warn('Configurações de e-mail não carregadas:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/super/email-settings`, settings, { headers: getSuperAdminHeader() });
      toast.success('Configurações de email salvas');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    setTestLoading(true);
    try {
      await axios.post(`${API}/super/test-email`, {}, { headers: getSuperAdminHeader() });
      toast.success('Email de teste enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar email de teste');
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Provider Selection */}
      <Card className="bg-white border-gray-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            Provedor de Email
          </CardTitle>
          <CardDescription>
            Escolha o serviço para envio de emails (relatórios, convites, notificações)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setSettings({ ...settings, provider: 'resend' })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                settings.provider === 'resend' 
                  ? 'border-indigo-600 bg-indigo-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-black flex items-center justify-center">
                  <span className="text-white font-bold text-sm">R</span>
                </div>
                <div>
                  <p className="font-medium">Resend</p>
                  <p className="text-xs text-gray-500">API moderna e simples</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setSettings({ ...settings, provider: 'smtp' })}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                settings.provider === 'smtp' 
                  ? 'border-indigo-600 bg-indigo-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Server className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="font-medium">SMTP</p>
                  <p className="text-xs text-gray-500">Servidor próprio</p>
                </div>
              </div>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Resend Settings */}
      {settings.provider === 'resend' && (
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-5 w-5 text-indigo-600" />
              Configuração Resend
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>API Key</Label>
              <Input 
                type="password"
                value={settings.resend_api_key}
                onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
                placeholder="re_xxxxxxxxxx"
              />
              <p className="text-xs text-gray-500">
                Obtenha sua API Key em <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">resend.com</a>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SMTP Settings */}
      {settings.provider === 'smtp' && (
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Server className="h-5 w-5 text-indigo-600" />
              Configuração SMTP
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Servidor SMTP</Label>
                <Input 
                  value={settings.smtp_host}
                  onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                  placeholder="smtp.exemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Porta</Label>
                <Input 
                  value={settings.smtp_port}
                  onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })}
                  placeholder="587"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Usuário</Label>
                <Input 
                  value={settings.smtp_user}
                  onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                  placeholder="usuario@exemplo.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Senha</Label>
                <Input 
                  type="password"
                  value={settings.smtp_password}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email Remetente</Label>
              <Input 
                value={settings.smtp_from}
                onChange={(e) => setSettings({ ...settings, smtp_from: e.target.value })}
                placeholder="noreply@exemplo.com"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                checked={settings.smtp_tls}
                onCheckedChange={(checked) => setSettings({ ...settings, smtp_tls: checked })}
              />
              <Label>Usar TLS/SSL</Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salvar Configurações
        </Button>
        <Button variant="outline" onClick={handleTestEmail} disabled={testLoading}>
          {testLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
          Enviar Email de Teste
        </Button>
      </div>
    </div>
  );
}

function SecuritySettings() {
  return (
    <Card className="bg-white border-gray-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-600" />
          Configurações de Segurança
        </CardTitle>
        <CardDescription>
          Configure as políticas de segurança da plataforma
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Funcionalidades avançadas de segurança em desenvolvimento. Em breve: 2FA, políticas de senha, logs de auditoria.
          </AlertDescription>
        </Alert>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <p className="font-medium">Sessões Ativas</p>
              <p className="text-sm text-gray-500">Tempo máximo de sessão: 12 horas</p>
            </div>
            <Badge className="bg-green-50 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Ativo
            </Badge>
          </div>
          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <p className="font-medium">Criptografia de Senhas</p>
              <p className="text-sm text-gray-500">bcrypt com salt</p>
            </div>
            <Badge className="bg-green-50 text-green-700">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Ativo
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Badge({ children, className }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}
